import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PhoneOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCall } from "@/contexts/CallContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoCallPage() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const { activeCall, joinCall, maximize } = useCall();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const isAdmin = user.role === "admin";
  const displayName = user.name || user.email || "Guest";

  useEffect(() => {
    const init = async () => {
      // If this call is already active in context, ensure we have a token
      if (activeCall?.roomName === roomName) {
        if (!activeCall.meetingToken && token) {
          try {
            const tokenRes = await axios.post(
              `${API}/video-calls/rooms/${roomName}/token`,
              { name: displayName, is_owner: isAdmin }
            );
            if (tokenRes.data?.token) {
              joinCall(roomName, activeCall.dailyUrl, tokenRes.data.token, activeCall.inviteeNames);
            }
          } catch {}
        }
        maximize();
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API}/video-calls/rooms/${roomName}`);
        const dailyUrl = res.data.daily_url;

        let meetingToken = null;
        if (token) {
          try {
            const tokenRes = await axios.post(
              `${API}/video-calls/rooms/${roomName}/token`,
              { name: displayName, is_owner: isAdmin }
            );
            meetingToken = tokenRes.data?.token;
          } catch (tokenErr) {
            const detail = tokenErr.response?.data?.detail || "";
            if (
              tokenErr.response?.status === 402 ||
              detail.toLowerCase().includes("payment")
            ) {
              setError(
                "Daily.co account needs a payment method. Add one at dashboard.daily.co/billing"
              );
              return;
            }
          }
        }

        const inviteeNames = res.data.invitee_names || [];
        joinCall(roomName, dailyUrl, meetingToken, inviteeNames);
      } catch {
        setError("Room not found or has expired");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [roomName]); // Only re-run when room changes

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#0F0F23" }}
      >
        <div className="text-center space-y-4 p-6">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
            <PhoneOff className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">{error}</h2>
          <Button
            onClick={() => navigate(-1)}
            className="bg-[#00D4FF] hover:bg-[#00B8E0] text-black font-medium"
            data-testid="call-error-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#0F0F23" }}
      >
        <Loader2 className="w-10 h-10 text-[#00D4FF] animate-spin" />
      </div>
    );
  }

  // FloatingCall (rendered in App.js) handles the actual call UI overlay.
  // This page just shows a dark background behind it.
  return (
    <div
      className="min-h-screen"
      style={{ background: "#0F0F23" }}
      data-testid="video-call-page"
    />
  );
}
